import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import LucideIcon from '@/components/LucideIcon';

/**
 * Fallback UI rendered when a module route throws a rendering error.
 * Shows the module identifier (when available), a retry button that
 * resets the error boundary, and a link back to the dashboard.
 */
function ModuleRouteFallback({ error, resetErrorBoundary }: FallbackProps) {
  const { moduleId } = useParams<{ moduleId: string }>();

  return (
    <div
      className="flex flex-col items-center justify-center gap-4 p-8 text-center"
      role="alert"
    >
      <LucideIcon name="AlertTriangle" className="h-12 w-12 text-destructive" />
      <h2 className="text-xl font-semibold">Something went wrong</h2>
      <p className="text-muted-foreground max-w-md">
        {moduleId
          ? `An error occurred while loading module "${moduleId}".`
          : 'An unexpected error occurred while loading this page.'}
      </p>
      {error instanceof Error && error.message && (
        <p className="text-sm text-muted-foreground/70 max-w-md break-words">
          {error.message}
        </p>
      )}
      <div className="flex gap-3">
        <Button variant="outline" onClick={resetErrorBoundary}>
          Retry
        </Button>
        <Button asChild>
          <Link to="/">Return to Dashboard</Link>
        </Button>
      </div>
    </div>
  );
}

interface ModuleErrorBoundaryProps {
  moduleId?: string;
  children: React.ReactNode;
}

/**
 * Error boundary wrapper for module routes.
 * Logs caught errors with the module identifier and stack trace,
 * then renders a user-friendly fallback with retry + dashboard link.
 */
export default function ModuleErrorBoundary({
  moduleId,
  children,
}: ModuleErrorBoundaryProps) {
  return (
    <ErrorBoundary
      FallbackComponent={ModuleRouteFallback}
      onError={(error, info) => {
        console.error(
          `[ModuleErrorBoundary] Error in module "${moduleId ?? 'unknown'}":`,
          error,
          info?.componentStack,
        );
      }}
      onReset={() => {
        // Reset is handled by react-error-boundary re-rendering children
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
